import React, { ReactElement, useState, useMemo } from 'react';
import Input from 'antd/es/input';
import Dropdown from 'antd/es/dropdown';
import Button from 'antd/es/button';
import Menu from 'antd/es/menu';
import Tag from 'antd/es/tag';
import { Icon } from '@digicert/dcone-common-ui';
import { Button as DcButton, ButtonType, ButtonSizeTypes, SplitButton, SplitButtonType, SplitButtonSizeType } from '@digicert/dcone-common-ui';
import {
  faSearch,
  faTimes,
  faGlobe,
  faChevronDown,
  faArrowUpRightFromSquare,
  faPlus,
} from '@fortawesome/pro-light-svg-icons'
import { ENVIRONMENTS_MOCK, Environment } from './environmentsMock';
import { EnvironmentCard } from './EnvironmentCard';
import { EnvironmentSideRail } from './EnvironmentSideRail';
import { STRINGS } from './environmentsStrings';
import styles from './environments.module.scss';

const Environments = (): ReactElement => {
  const [searchText, setSearchText] = useState('');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  // TODO: CertCentral filter logic not clear — leave as UI stub for now
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(null);
  const [sideRailVisible, setSideRailVisible] = useState(false);

  // Derive unique region options from mock data
  const regionOptions = useMemo(
    () => Array.from(new Set(ENVIRONMENTS_MOCK.map((e) => e.region))),
    []
  );

  // Derive unique product options from mock data
  const productOptions = useMemo(
    () =>
      Array.from(
        new Set(ENVIRONMENTS_MOCK.flatMap((e) => e.applications.map((a) => a.name)))
      ),
    []
  );

  const filteredEnvironments = useMemo(() => {
    return ENVIRONMENTS_MOCK.filter((env) => {
      const matchesSearch = env.name.toLowerCase().includes(searchText.toLowerCase());
      const matchesRegion =
        selectedRegions.length === 0 || selectedRegions.includes(env.region);
      const matchesProduct =
        selectedProducts.length === 0 ||
        env.applications.some((app) => selectedProducts.includes(app.name));
      return matchesSearch && matchesRegion && matchesProduct;
    });
  }, [searchText, selectedRegions, selectedProducts]);

  const handleCardSelect = (env: Environment) => {
    setSelectedEnv(env);
    setSideRailVisible(true);
  };

  const handleSideRailDismiss = () => {
    setSideRailVisible(false);
    setSelectedEnv(null);
  };

  const toggleFilter = (
    value: string,
    selected: string[],
    setSelected: (v: string[]) => void
  ) => {
    if (selected.includes(value)) {
      setSelected(selected.filter((v) => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  const clearAllFilters = () => {
    setSelectedRegions([]);
    setSelectedProducts([]);
  };

  const regionMenuItems = regionOptions.map((region) => ({
    key: region,
    label: (
      <span>
        {selectedRegions.includes(region) ? '✓ ' : ''}{region}
      </span>
    ),
    onClick: () => toggleFilter(region, selectedRegions, setSelectedRegions),
  }));

  const productMenuItems = productOptions.map((product) => ({
    key: product,
    label: (
      <span>
        {selectedProducts.includes(product) ? '✓ ' : ''}{product}
      </span>
    ),
    onClick: () => toggleFilter(product, selectedProducts, setSelectedProducts),
  }));

  const handleLinkEnvironment = () => {
    // TODO: add link environment callback
  };

  const handleRequestEnvironment = () => {
    // TODO: add request environment callback — open external request URL
  };

  const hasActiveFilters = selectedRegions.length > 0 || selectedProducts.length > 0;

  return (
    <div className={styles.environmentsPage}>
      <h1 className={styles.pageTitle}>{STRINGS.PAGE_TITLE}</h1>

      {/* ───── Top Controls ───── */}
      <div className={styles.controls}>
        <div className={styles.searchWrapper}>
          <Input
            prefix={<Icon icon={faSearch} />}
            suffix={
              searchText ? (
                <Icon
                  icon={faTimes}
                  onClick={() => setSearchText('')}
                  style={{ cursor: 'pointer' }}
                />
              ) : null
            }
            placeholder={STRINGS.SEARCH_PLACEHOLDER}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filters}>
          <Dropdown menu={{ items: regionMenuItems }} trigger={['click']}>
            <Button className={styles.filterBtn}>
              <Icon icon={faGlobe} /> {STRINGS.REGION_FILTER_LABEL} <Icon icon={faChevronDown} />
            </Button>
          </Dropdown>

          <Dropdown menu={{ items: productMenuItems }} trigger={['click']}>
            <Button className={styles.filterBtn}>
              {STRINGS.PRODUCTS_FILTER_LABEL} <Icon icon={faChevronDown} />
            </Button>
          </Dropdown>

          {/* TODO: CertCentral filter logic not determined — stub only */}
          <Dropdown menu={{ items: [] }} trigger={['click']}>
            <Button className={styles.filterBtn}>
              {STRINGS.CERTCENTRAL_FILTER_LABEL} <Icon icon={faChevronDown} />
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* ───── Active Filter Pills ───── */}
      {hasActiveFilters && (
        <div className={styles.filterPills}>
          {selectedRegions.map((region) => (
            <Tag
              key={`region-${region}`}
              closable
              onClose={() => toggleFilter(region, selectedRegions, setSelectedRegions)}
            >
              {STRINGS.FILTER_REGION_PREFIX}: {region}
            </Tag>
          ))}
          {selectedProducts.map((product) => (
            <Tag
              key={`product-${product}`}
              closable
              onClose={() => toggleFilter(product, selectedProducts, setSelectedProducts)}
            >
              {STRINGS.FILTER_PRODUCTS_PREFIX}: {product}
            </Tag>
          ))}
          <button className={styles.clearAllBtn} onClick={clearAllFilters}>
            {STRINGS.CLEAR_ALL_FILTERS} ×
          </button>
        </div>
      )}

      {/* ───── Environment Grid ───── */}
      <div className={styles.envGrid}>
        {filteredEnvironments.map((env) => (
          <EnvironmentCard
            key={env.id}
            environment={env}
            onSelect={handleCardSelect}
          />
        ))}

        {/* "Add an environment" dashed card */}
        <div className={styles.addEnvCard}>
          <div className={styles.addEnvContent}>
            <div className={styles.addEnvIcon}>
              <Icon icon={faPlus} />
            </div>
            <div>
              <div className={styles.addEnvTitle}>{STRINGS.ADD_ENV_TITLE}</div>
              <div className={styles.addEnvDescription}>{STRINGS.ADD_ENV_DESCRIPTION}</div>
            </div>
          </div>
          <div className={styles.addEnvActions}>
            <SplitButton
              type={SplitButtonType.PRIMARY}
              onClick={handleLinkEnvironment}
              size={SplitButtonSizeType.STANDARD}
              actionMenu={
                <Menu
                  items={[
                    {
                      key: 'link-environment',
                      label: STRINGS.LINK_ENVIRONMENT,
                      onClick: handleLinkEnvironment,
                    },
                  ]}
                />
              }
              label={STRINGS.LINK_ENVIRONMENT}
            />
            <DcButton
              buttonType={ButtonType.SECONDARY}
              onClick={handleRequestEnvironment}
              size={ButtonSizeTypes.DEFAULT}
              icon={<Icon icon={faArrowUpRightFromSquare} />}
            >
              {STRINGS.REQUEST_ENVIRONMENT}
            </DcButton>
          </div>
        </div>
      </div>

      {/* ───── Side Rail ───── */}
      <EnvironmentSideRail
        environment={selectedEnv}
        show={sideRailVisible}
        onDismiss={handleSideRailDismiss}
      />
    </div>
  );
};

export default Environments;
